import {
  ExtractorResponse,
  ExtractorResult,
} from "../../../../common/entities/type";
import { isWithinRange, ResolvedRange } from "./keragonDateRange";
import {
  BiomarkerStatus,
  CatalogInfo,
  isPersisted,
  NormalizedBiomarker,
  normalizeResults,
} from "../../utils/extractorResults";

/**
 * Pure model behind step 2 of the "Sync EHR data" modal.
 *
 * The Keragon result poll returns the extractor's shape (one top-level
 * measurementDate); splitResultByDate turns it into one response per date and
 * every persisted (importable) biomarker of every date becomes one row, keyed
 * by biomarker + date. The same rows are grouped either by biomarker
 * ("Biomarkers" view) or by date ("Labs" view), and one selection set is shared
 * by both views so switching never loses what the affiliate ticked.
 */

export type KeragonRow = {
  rowKey: string; // `${biomarkerID}|${date}` (+ `#${n}` for a same-date duplicate)
  biomarkerID: string;
  date: string; // measurementDate, yyyy-MM-dd
  displayName: string;
  displayValue: string;
  displayUnit: string;
  status: BiomarkerStatus; // always a persisted status here
  isNew: boolean; // not already recorded for this client on this date
  keragonId: string | null; // saved as the reading's batchId
  item: NormalizedBiomarker; // source of the import payload
};

export type BiomarkerGroup = {
  biomarkerID: string;
  name: string;
  unit: string;
  rows: KeragonRow[];
  newCount: number;
};

export type DateGroup = { date: string; rows: KeragonRow[] };

export type BuiltRows = {
  rows: KeragonRow[];
  unmatchedCount: number;
  rejectedCount: number;
  // The skipped readings themselves, listed in the step-2 notices.
  unmatchedItems: NormalizedBiomarker[];
  rejectedItems: NormalizedBiomarker[];
};

/** `${biomarkerID}|${yyyy-MM-dd}` — also the shape of `existingKeys`. */
export const existingKey = (biomarkerID: string, date: string) =>
  `${biomarkerID}|${date.slice(0, 10)}`;

export const buildRows = (
  responses: ExtractorResponse[],
  catalogById: Map<string, CatalogInfo>,
  existingKeys: Set<string>
): BuiltRows => {
  const rows: KeragonRow[] = [];
  const unmatchedItems: NormalizedBiomarker[] = [];
  const rejectedItems: NormalizedBiomarker[] = [];
  const seen = new Map<string, number>();

  responses.forEach((response) => {
    const date = response.measurementDate;
    normalizeResults(response.results, catalogById).forEach((item) => {
      if (!isPersisted(item) || !item.biomarkerID) {
        if (item.status === "unmatched") unmatchedItems.push(item);
        else rejectedItems.push(item);
        return;
      }
      // A qualitative value that matched no catalog option has nothing to
      // import; it is neither shown nor counted against the affiliate.
      if (item.status === "qualitative" && !item.displayValue) return;

      const base = existingKey(item.biomarkerID, date);
      const dup = seen.get(base) ?? 0;
      seen.set(base, dup + 1);
      rows.push({
        rowKey: dup === 0 ? base : `${base}#${dup}`,
        biomarkerID: item.biomarkerID,
        date,
        displayName: item.displayName,
        displayValue: item.displayValue,
        displayUnit: item.displayUnit,
        status: item.status,
        isNew: !existingKeys.has(base),
        keragonId: item.keragonId,
        item,
      });
    });
  });

  return {
    rows,
    unmatchedCount: unmatchedItems.length,
    rejectedCount: rejectedItems.length,
    unmatchedItems,
    rejectedItems,
  };
};

/**
 * The documented result has one top-level `measurementDate` (the most recent
 * test). If rows ever carry their own date, group by it so each reading keeps
 * its real date; otherwise everything lands on the top-level date.
 */
export const splitResultByDate = (
  result: ExtractorResponse | null | undefined
): ExtractorResponse[] => {
  if (!result) return [];
  const groups = new Map<string, ExtractorResult[]>();
  (result.results ?? []).forEach((r) => {
    const own = (r as ExtractorResult & { date?: string; measurementDate?: string });
    const date = String(own.date || own.measurementDate || result.measurementDate || "").slice(0, 10);
    groups.set(date, [...(groups.get(date) ?? []), r]);
  });
  return [...groups.entries()].map(([measurementDate, results]) => ({
    measurementDate,
    results,
  }));
};

/** Drops readings outside the chosen range (the trigger has no `until`). */
export const filterRowsByRange = (rows: KeragonRow[], range: ResolvedRange) =>
  rows.filter((row) => isWithinRange(row.date, range));

const byDateDesc = (a: KeragonRow, b: KeragonRow) => b.date.localeCompare(a.date);

/** One group per biomarker, A–Z; rows newest first. */
export const groupByBiomarker = (rows: KeragonRow[]): BiomarkerGroup[] => {
  const groups = new Map<string, BiomarkerGroup>();
  rows.forEach((row) => {
    const group = groups.get(row.biomarkerID) ?? {
      biomarkerID: row.biomarkerID,
      name: row.displayName,
      unit: row.displayUnit,
      rows: [],
      newCount: 0,
    };
    group.rows.push(row);
    if (row.isNew) group.newCount += 1;
    groups.set(row.biomarkerID, group);
  });
  return [...groups.values()]
    .map((g) => ({ ...g, rows: [...g.rows].sort(byDateDesc) }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

/** One group per date, newest first; rows A–Z within the date. */
export const groupByDate = (rows: KeragonRow[]): DateGroup[] => {
  const groups = new Map<string, KeragonRow[]>();
  rows.forEach((row) => {
    groups.set(row.date, [...(groups.get(row.date) ?? []), row]);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, groupRows]) => ({
      date,
      rows: [...groupRows].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    }));
};

export type GroupCheckState = "none" | "some" | "all";

export const groupCheckState = (
  selected: Set<string>,
  keys: string[]
): GroupCheckState => {
  if (keys.length === 0) return "none";
  const count = keys.filter((k) => selected.has(k)).length;
  if (count === 0) return "none";
  return count === keys.length ? "all" : "some";
};

export const toggleRow = (selected: Set<string>, key: string): Set<string> => {
  const next = new Set(selected);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
};

/** A partially or fully selected group clears; an empty one selects all. */
export const toggleGroup = (selected: Set<string>, keys: string[]): Set<string> => {
  const next = new Set(selected);
  if (groupCheckState(selected, keys) === "all") keys.forEach((k) => next.delete(k));
  else keys.forEach((k) => next.add(k));
  return next;
};

export const selectAll = (rows: KeragonRow[]): Set<string> =>
  new Set(rows.map((r) => r.rowKey));

export const formatRowValue = (row: KeragonRow) =>
  `${row.displayValue}${row.displayUnit ? ` ${row.displayUnit}` : ""}`;
